import React from 'react';
import PropTypes from 'prop-types';
import { Grid } from '@material-ui/core';
import { v4 as uuidv4 } from 'uuid';
import Fees from './fees';
import PaymentMethod from './paymentMethod';
import { CardAPI } from '../../../../API/API-Card';
import { ProgramRegistrationAPI } from '../../../../API/API-programRegistration';
import Alert from '../../../../materialUi/Alerts';

const normalizeID = (value) => {
  if (!value) return '';
  // eslint-disable-next-line no-underscore-dangle
  if (value._id) return value._id.toString();
  return value.toString();
};

export const hasExistingRegistration = (
  registrations,
  selectedFamilyMembers,
  selectedWeeks,
  weeklyProgram,
) => {
  if (!Array.isArray(registrations) || !Array.isArray(selectedFamilyMembers)) return false;

  const selectedFamilyMemberIDs = new Set(selectedFamilyMembers.map(normalizeID));
  const selectedWeekIDs = new Set((selectedWeeks || []).map(normalizeID));

  return registrations.some((registration) => {
    if (String(registration.paymentStatus || '').toLowerCase() !== 'approved') return false;

    const sameParticipant = (registration.familyMembers || []).some(
      (familyMember) => selectedFamilyMemberIDs.has(normalizeID(familyMember)),
    );
    if (!sameParticipant) return false;
    if (!weeklyProgram) return true;

    return (registration.selectedWeeks || []).some(
      (week) => selectedWeekIDs.has(normalizeID(week)),
    );
  });
};

function Payment({
  data, programData, handleNextStep, userData,
}) {
  const [openAlert, setOpenAlert] = React.useState(false);
  const [alertValues, setAlertValues] = React.useState({});
  const [paymentStatus, setPaymentStatus] = React.useState(false);
  const paymentAttemptSessionKey = [
    `programPaymentAttempt_${programData.programID}`,
    data.membership.membershipID,
  ].join('_');
  const [paymentAttemptID, setPaymentAttemptID] = React.useState(() => {
    const savedPaymentAttemptID = sessionStorage.getItem(paymentAttemptSessionKey);
    if (savedPaymentAttemptID) return savedPaymentAttemptID;
    const newPaymentAttemptID = uuidv4();
    sessionStorage.setItem(paymentAttemptSessionKey, newPaymentAttemptID);
    return newPaymentAttemptID;
  });

  const resetPaymentAttemptID = () => {
    const newPaymentAttemptID = uuidv4();
    sessionStorage.setItem(paymentAttemptSessionKey, newPaymentAttemptID);
    setPaymentAttemptID(newPaymentAttemptID);
  };

  const getWeeklyVariableFee = () => {
    if (data.membership.membershipType.toLowerCase() === 'associate') {
      return programData.associateFee * data.selectedWeeks.length;
    }
    return programData.communityFee * data.selectedWeeks.length;
  };

  const getWeeklyProgramFee = () => {
    if (programData.variableFee.toLowerCase() === 'yes') return getWeeklyVariableFee();
    return programData.fee * data.selectedWeeks.length;
  };

  const getRegularProgramVariableFee = () => {
    if (data.membership.membershipType.toLowerCase() === 'associate') {
      return programData.associateFee;
    }
    return programData.communityFee;
  };

  const getRegularProgramFee = () => {
    if (programData.variableFee && programData.variableFee.toLowerCase() === 'yes') return getRegularProgramVariableFee();
    return programData.fee;
  };

  const getProgramFee = () => {
    let fee = 0;
    if (programData.weeklyProgram) {
      fee = getWeeklyProgramFee();
    } else {
      fee = getRegularProgramFee()
    }
    const participants = data.familyMembers && data.familyMembers.length;
    return fee * participants;
  };

  const [calculatedFee, setCalculatedFee] = React.useState(null);

  const handleFee = (newFee) => {
    if (newFee === 'Free') return setCalculatedFee(newFee);
    return setCalculatedFee(parseFloat(newFee));
  };

  const initialFee = getProgramFee();

  const getFinalFee = () => {
    if (calculatedFee === null) {
      return initialFee
    }
    return calculatedFee === 'Free' ? 0 : calculatedFee;
  };

  const addReceiptInformation = (paymentMethod, cardType) => {
    const copyData = data;
    copyData.receipt = {
      membershipID: data.membership.membershipID,
      fee: getFinalFee(),
      paymentMethod,
      cardType,
      referenceNumber: Math.floor(100000 + Math.random() * 90000000),
      name: `${data.membership.familyMembersInformation[0].firstName} ${data.membership.familyMembersInformation[0].lastName}`,
      status: 'Approved',
      category: 'Program Registration',
      address: data.membership.address,
      paymentDate: new Date(),
      description: programData.name
    };
    return copyData;
  };

  const submitProgramRegistration = async (newData) => {
    data.fee = calculatedFee === 'Free' ? 0 : calculatedFee;
    if (!calculatedFee) {
      data.fee = initialFee
    }
    try {
      const response = await ProgramRegistrationAPI.POST.addNewProgramRegistration(newData);
      console.log(response);
      handleNextStep(data, true);
      data.currentStep = 2;
      sessionStorage.setItem(`programRegistration_${programData.programID}`, JSON.stringify(data));
      sessionStorage.removeItem(paymentAttemptSessionKey);
      return true;
    } catch (error) {
      console.log(error);
      setOpenAlert(true);
      setAlertValues({
        alertTitle: 'PROGRAM REGISTRATION ERROR!',
        body: 'Payment was approved, but the program registration could not be completed. '
          + 'Please contact SCCC and do not submit another payment.',
        leave: 'hidden',
        stay: 'CLOSE',
      });
      return false;
    }
  };

  const submitCashPayment = async (type) => {
    const newData = addReceiptInformation(type, type);
    await submitProgramRegistration(newData);
  };

  const handleApprovedCardPayment = async (responseData) => {
    const cardType = responseData.result.payment.card_details.card.card_brand;
    const newData = addReceiptInformation('Card', cardType);
    newData.membership.cardType = cardType;
    newData.membership.cardInformation = responseData.result.payment;
    await submitProgramRegistration(newData);
  };

  const formateCardPaymentFee = (fee) => {
    return fee.toString().includes('.') ? fee.toString().replace('.', '') : `${fee.toString()}00`;
  };

  const registrationAlreadyExists = async () => {
    try {
      const response = await ProgramRegistrationAPI.GET.getProgramRegistrationDetails(
        programData.programID,
        data.membership.membershipID,
      );
      return hasExistingRegistration(
        response.data,
        data.familyMembers,
        data.selectedWeeks,
        programData.weeklyProgram,
      );
    } catch (error) {
      const noRegistrationsFound = error.response
        && error.response.status === 400
        && error.response.data
        && !error.response.data.error;
      if (noRegistrationsFound) return false;
      throw error;
    }
  };

  const submitCardPayment = async (nonce, cardHolderName) => {
    try {
      if (await registrationAlreadyExists()) {
        setPaymentStatus(true);
        setOpenAlert(true);
        setAlertValues({
          alertTitle: 'PROGRAM REGISTRATION ERROR!',
          body: 'Family Member Already registered to this program.',
          leave: 'hidden',
          stay: 'CLOSE',
        });
        return;
      }
    } catch (error) {
      console.log(error);
      setPaymentStatus(true);
      setOpenAlert(true);
      setAlertValues({
        alertTitle: 'PROGRAM REGISTRATION ERROR!',
        body: 'Unable to verify the program registration. '
          + 'No payment was processed. Please try again.',
        leave: 'hidden',
        stay: 'CLOSE',
      });
      return;
    }

    await CardAPI.Square.cardPayment(
      nonce,
      data.membership.membershipID,
      `Program Registration,  Membership: ${data.membership.membershipID}, Card Holder Name: ${cardHolderName}`,
      `${formateCardPaymentFee(calculatedFee || initialFee)}`,
      paymentAttemptID,
    )
      .then(async (response) => {
        await handleApprovedCardPayment(response.data);
        // SetStep to show success message
      })
      .catch((error) => {
        if (error.response) resetPaymentAttemptID();
        setPaymentStatus(true);
        console.log(error);
        setOpenAlert(true);
        setAlertValues({
          alertTitle: 'PAYMENT ERROR!',
          body: 'Payment Declined. Please, check the data entered.',
          leave: 'hidden',
          stay: 'CLOSE',
        });
      });
  };

  const handleSubmitPayment = async (paymentObj) => {
    const { type, nonce, cardHolderName } = paymentObj;
    if (type === 'card') {
      await submitCardPayment(nonce, cardHolderName);
    } else {
      await submitCashPayment(type);
    }
  };

  return (
    <Grid container className="programRegistration-wrapper">
      <Grid item xs={12} className="Payment-title">
        Payment
      </Grid>
      <Grid item xs={12}>
        <Grid container spacing={2}>
          <Fees
            data={data}
            programData={programData}
            fee={calculatedFee || initialFee}
          />
          <PaymentMethod
            submitPayment={handleSubmitPayment}
            handleFee={handleFee}
            initialFee={getProgramFee()}
            userData={userData}
            paymentStatus={paymentStatus}
          />
        </Grid>
      </Grid>
      <Alert
        open={openAlert}
        close={() => setOpenAlert(false)}
        location="/"
        title={alertValues.alertTitle}
        body={alertValues.body}
        stay={alertValues.stay}
        leave={alertValues.leave}
      />
    </Grid>
  );
}

Payment.propTypes = {
  handleNextStep: PropTypes.func.isRequired,
  userData: PropTypes.shape({
    userData: PropTypes.shape({
      userType: PropTypes.string,
    }),
  }).isRequired,
  data: PropTypes.shape(
    {
      selectedWeeks: PropTypes.arrayOf(PropTypes.any),
      membership: PropTypes.shape(
        {
          membershipCategory: PropTypes.string,
          membershipType: PropTypes.string,
          fee: PropTypes.number,
          membershipID: PropTypes.string,
          address: PropTypes.objectOf(PropTypes.any),
          familyMembersInformation: PropTypes.arrayOf(PropTypes.any),
        },
      ),
    },
  ).isRequired,
  programData: PropTypes.shape(
    {
      name: PropTypes.string,
      variableFee: PropTypes.string,
      associateFee: PropTypes.string,
      communityFee: PropTypes.string,
      fee: PropTypes.number,
      weeklyProgram: PropTypes.bool,
    },
  ).isRequired,
};

export default Payment;
